// src/components/Profile.jsx
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "react-oauth2-code-pkce";
import axios from "axios";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Profile component
 * - Styling adjusted to match RegisterMember look (clean card, inputs, buttons)
 * - Photo on left, sections on right
 * - All original logic kept intact
 * - Helper functions moved to bottom of file
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export default function Profile() {
  const auth = useContext(AuthContext);
  const token = auth?.token;
  const apiToken = token || "";
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    id: "",
    firstName: "",
    lastName: "",
    gender: "",
    birthDate: null,
    marriageDate: null,
    deathDate: null,
    memberAlive: true,
    bioNotes: "",
    photo: "",
    photoFile: undefined,
    email: "",
    facebook: "",
    twitter: "",
    instagram: "",
    mobile: "",
    homeTel: "",
    website: "",
    profession: "",
    company: "",
    interests: "",
    birthPlace: "",
    deathPlace: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Redirect toast param
  const location = useLocation();
  const forceUpdate = location.search.includes("forceUpdate=true");

  useEffect(() => {
    if (forceUpdate) {
     toast.custom(
    () => (
      <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-300 text-yellow-900 shadow-md rounded p-3 w-80">
        <div className="text-xl">⚠️</div>
        <div className="flex-1">
          <div className="font-semibold">Profile Update Required</div>
          <div className="text-sm">
             Please edit your profile and provide as much information as possible.
          </div>
        </div>
      </div>
    ),
    {
      duration: 4500,
      position: "top-center",
    }
  );
    }
  }, [forceUpdate]);

  // Fetch profile (fixed: wait for auth.loginInProgress and token)
  useEffect(() => {
    if (auth.loginInProgress) return;
    if (!apiToken) return;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${apiToken}` },
        });

        const d = res.data?.data || res.data || {};

        setProfile((prev) => ({
          ...prev,
          id: d.id ?? prev.id,
          firstName: d.firstName ?? prev.firstName,
          lastName: d.lastName ?? prev.lastName,
          gender: d.gender ?? prev.gender,
          birthDate: toLocalizedDate(d.birthDate) || null,
          marriageDate: toLocalizedDate(d.marriageDate) || null,
          deathDate: toLocalizedDate(d.deathDate) || null,
          memberAlive:
            typeof d.memberAlive === "boolean"
              ? d.memberAlive
              : d.memberAlive === "true",
          bioNotes: d.bioNotes ?? prev.bioNotes,
          photo: d.photo ?? d.avatar ?? prev.photo,
          email: d.email ?? prev.email,
          facebook: d.facebook ?? prev.facebook,
          twitter: d.twitter ?? prev.twitter,
          instagram: d.instagram ?? prev.instagram,
          mobile: d.mobile ?? prev.mobile,
          homeTel: d.homeTel ?? prev.homeTel,
          website: d.website ?? prev.website,
          profession: d.profession ?? prev.profession,
          company: d.company ?? prev.company,
          interests: d.interests ?? prev.interests,
          birthPlace: d.birthPlace ?? prev.birthPlace,
          deathPlace: d.deathPlace ?? prev.deathPlace,
        }));
      } catch (err) {
        console.error("Failed to load profile:", err);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [apiToken, auth.loginInProgress]);

  // --- Handlers (logic unchanged) ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setProfile((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === "birthDate" || name === "marriageDate" || name === "deathDate") {
      const ld = toLocalizedDate(value);
      setProfile((prev) => ({ ...prev, [name]: ld }));
      return;
    }

    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () =>
      setProfile((prev) => ({ ...prev, photo: reader.result, photoFile: file }));
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setProfile((prev) => ({ ...prev, photo: "", photoFile: undefined }));
  };

  const handleSave = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!apiToken) {
      toast.error("Not authenticated");
      return;
    }

    setSaving(true);
    try {
      const form = new FormData();

      const fields = [
        "id",
        "firstName",
        "lastName",
        "gender",
        "email",
        "bioNotes",
        "mobile",
        "homeTel",
        "website",
        "profession",
        "company",
        "interests",
        "birthPlace",
        "deathPlace",
        "facebook",
        "twitter",
        "instagram",
      ];
      fields.forEach((f) => {
        const val = profile[f];
        if (val !== undefined && val !== null && val !== "") {
          form.append(f, String(val));
        }
      });

      form.append("memberAlive", profile.memberAlive ? "true" : "false");

      const appendLocalizedDate = (name, localizedDateObj) => {
        if (localizedDateObj) {
          form.append(`${name}.localized`, localizedDateObj.localized || "");
          form.append(`${name}.value`, localizedDateObj.value || "");
          form.append(`${name}.culture`, localizedDateObj.culture || "");
          form.append(`${name}.timeZone`, localizedDateObj.timeZone || "");
          form.append(`${name}.raw`, String(localizedDateObj.raw) || "");
        }
      };

      appendLocalizedDate("birthDate", profile.birthDate);
      appendLocalizedDate("marriageDate", profile.marriageDate);
      appendLocalizedDate("deathDate", profile.deathDate);

      if (profile.photoFile instanceof File) {
        form.append("photo", profile.photoFile);
        form.append("avatar", "");
      } else {
        const photoValue =
          profile.photo && !profile.photo.startsWith("data:") ? profile.photo : "";
        form.append("avatar", photoValue);
      }
      form.append("updatedProfile", true);
      const res = await axios.patch(`${API_BASE_URL}/user/profile`, form, {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      });
toast.custom(
  (t) => (
    <div
      className={`${
        t.visible ? "animate-slide-in" : "animate-slide-out"
      } max-w-sm w-full bg-white border border-success/40 shadow-lg rounded-md p-4 ring-1 ring-black ring-opacity-5`}
    >
      <div className="flex gap-3">
        <div className="text-green-600 text-xl mt-1">✔️</div>

        <div className="flex-1">
          <div className="font-semibold text-gray-900 text-sm">
            Profile Updated Successfully
          </div>

          <div className="text-xs text-gray-700 mt-1 leading-5">
            Your profile information has been saved.  
            You’ll be redirected to your dashboard shortly.
            <br />
            <span className="text-gray-600">
              If you wish to update your profile again, click the user icon in the header.
            </span>
          </div>
        </div>
      </div>
    </div>
  ),
  {
    duration: 4000,
    position: "top-center",
  }
);

// Auto redirect after 3 seconds
setTimeout(() => navigate("/dashboard"), 3000);


      const returned = res.data?.data || res.data || null;
      if (returned) {
        setProfile((prev) => ({
          ...prev,
          firstName: returned.firstName ?? prev.firstName,
          lastName: returned.lastName ?? prev.lastName,
          birthDate: toLocalizedDate(returned.birthDate) || prev.birthDate,
          marriageDate: toLocalizedDate(returned.marriageDate) || prev.marriageDate,
          deathDate: toLocalizedDate(returned.deathDate) || prev.deathDate,
          memberAlive:
            typeof returned.memberAlive === "boolean" ? returned.memberAlive : prev.memberAlive,
          photo: returned.photo ?? returned.avatar ?? prev.photo,
          email: returned.email ?? prev.email,
        }));
      }

      setEditMode(false);

    } catch (err) {
      console.error("Failed to update profile", err);
      const msg =
        err?.response?.data?.message || err?.response?.data?.error || "Failed to update profile";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // --- Render loading ---
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-blue-700 font-semibold">
        Loading profile...
      </div>
    );
  }

  // --- Main render (styled like RegisterMember) ---
  return (
  <div className="min-h-screen w-full bg-gradient-to-b from-purple-50 via-white to-purple-100 py-10 px-4">

    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-auto p-6 sm:p-8 my-6 overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Profile</h2>

        {!editMode ? (
          <button
            onClick={() => setEditMode(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
          >
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setEditMode(false)}
              disabled={saving}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-md"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-6">
        {/* PHOTO (left) */}
        <div className="w-full sm:w-40 flex-shrink-0 flex flex-col items-center">
          <div className="w-32 h-32 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden mb-3">
            {profile.photo ? (
              <img src={profile.photo} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <svg className="w-16 h-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 14c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            )}
          </div>

          {editMode && (
            <>
              <input id="profile-photo" type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
              <label
                htmlFor="profile-photo"
                className="inline-flex items-center justify-center w-full px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 cursor-pointer"
              >
                Upload Photo
              </label>

              <button
                type="button"
                onClick={handleRemovePhoto}
                className="mt-2 text-xs text-gray-600 hover:underline"
              >
                Remove Photo
              </button>
            </>
          )}

          <div className="text-center mt-3">
            <div className="text-sm font-semibold text-gray-800">{profile.firstName} {profile.lastName}</div>
            <div className="text-xs text-gray-500">{profile.email}</div>
          </div>
        </div>

        {/* DETAILS (right) */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Basic Details */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Details</h3>

            {!editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <FieldView label="First Name" value={profile.firstName} />
                <FieldView label="Last Name" value={profile.lastName} />
                <FieldView label="Gender" value={profile.gender} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <FieldInput label="First Name" name="firstName" value={profile.firstName} onChange={handleChange} />
                <FieldInput label="Last Name" name="lastName" value={profile.lastName} onChange={handleChange} />
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    name="gender"
                    value={profile.gender}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 text-xs px-3 focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            )}
          </section>

          {/* Important Dates */}
          <section>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Important Dates</h3>
            </div>

            {/* Professional Helper Message */}
            <p className="text-xs text-gray-600 mb-4">
              Adding your birth, marriage, or other key dates helps family members stay
              informed about important events and celebrations.
            </p>

            {!editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <FieldView label="Birth Date" value={profile.birthDate?.value} />
                <FieldView label="Marriage Date" value={profile.marriageDate?.value} />
                {!profile.memberAlive && <FieldView label="Death Date" value={profile.deathDate?.value} />}
                {!profile.memberAlive && <FieldView label="Death Place" value={profile.deathPlace} />}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FieldDate label="Birth Date" name="birthDate" value={profile.birthDate} onChange={handleChange} />
                  <FieldDate label="Marriage Date" name="marriageDate" value={profile.marriageDate} onChange={handleChange} />

                  {!profile.memberAlive && (
                    <FieldDate label="Death Date" name="deathDate" value={profile.deathDate} onChange={handleChange} />
                  )}
                </div>

                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="memberAlive"
                        checked={profile.memberAlive === true}
                        onChange={() => setProfile((p) => ({ ...p, memberAlive: true }))}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="text-xs text-gray-700">Alive</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="memberAlive"
                        checked={profile.memberAlive === false}
                        onChange={() => setProfile((p) => ({ ...p, memberAlive: false }))}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="text-xs text-gray-700">Deceased</span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </section>


          {/* Contact Info */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h3>

            {!editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldView label="Mobile" value={profile.mobile} />
                <FieldView label="Home Tel" value={profile.homeTel} />
                <FieldView label="Email" value={profile.email} />
                <FieldView label="Website" value={profile.website} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldInput label="Mobile" name="mobile" value={profile.mobile} onChange={handleChange} />
                <FieldInput label="Home Tel" name="homeTel" value={profile.homeTel} onChange={handleChange} />
                <FieldInput label="Email" name="email" value={profile.email} onChange={handleChange} />
                <FieldInput label="Website" name="website" value={profile.website} onChange={handleChange} />
              </div>
            )}
          </section>

          {/* Professional */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Professional</h3>

            {!editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldView label="Profession" value={profile.profession} />
                <FieldView label="Company" value={profile.company} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldInput label="Profession" name="profession" value={profile.profession} onChange={handleChange} />
                <FieldInput label="Company" name="company" value={profile.company} onChange={handleChange} />
              </div>
            )}
          </section>

          {/* Social Links */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Social Links</h3>

            {!editMode ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldView label="Facebook" value={profile.facebook} />
                <FieldView label="Twitter" value={profile.twitter} />
                <FieldView label="Instagram" value={profile.instagram} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldInput label="Facebook" name="facebook" value={profile.facebook} onChange={handleChange} />
                <FieldInput label="Twitter" name="twitter" value={profile.twitter} onChange={handleChange} />
                <FieldInput label="Instagram" name="instagram" value={profile.instagram} onChange={handleChange} />
              </div>
            )}
          </section>

          {/* Additional Info */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Additional Information</h3>

            {!editMode ? (
              <>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Bio Notes</div>
                  <div className="text-sm text-gray-700">{profile.bioNotes || "-"}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <FieldView label="Interests" value={profile.interests} />
                  <FieldView label="Birth Place" value={profile.birthPlace} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bio Notes</label>
                  <textarea
                    name="bioNotes"
                    value={profile.bioNotes}
                    onChange={handleChange}
                    rows={3}
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 text-xs px-3 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <FieldInput label="Interests" name="interests" value={profile.interests} onChange={handleChange} />
                  <FieldInput label="Birth Place" name="birthPlace" value={profile.birthPlace} onChange={handleChange} />
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
    </div>
  );
}

/* ---------------------------
   Small helper components
   (purely presentation)
   --------------------------- */

const FieldView = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-400">{label}</div>
    <div className="font-semibold text-sm text-gray-800">{value || "-"}</div>
  </div>
);

const FieldInput = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="text"
      name={name}
      value={value || ""}
      onChange={onChange}
      className="block w-full rounded-md border border-gray-300 shadow-sm py-2 text-xs px-3 focus:border-blue-500 focus:ring-blue-500"
    />
  </div>
);

const FieldDate = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="date"
      name={name}
      value={getInputDateValue(value)}
      onChange={onChange}
      className="block w-full rounded-md border border-gray-300 shadow-sm py-2 text-xs px-3 focus:border-blue-500 focus:ring-blue-500"
    />
  </div>
);

/* ---------------------------------------
   Helper functions kept at bottom (moved)
   --------------------------------------- */

function toLocalizedDate(d) {
  if (!d && d !== 0) return null;

  // If it's already a LocalizedDate-like object, return as-is (with cleanup)
  if (typeof d === "object" && d.value) {
    const parsedDate = new Date(d.value);
    return {
      localized: d.localized ?? parsedDate.toLocaleDateString(),
      value: d.value,
      culture: d.culture ?? navigator.language,
      timeZone: d.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      raw: typeof d.raw === "number" ? d.raw : parsedDate.getTime(),
    };
  }

  // If it's a string or number, try to parse as date
  if (typeof d === "string" || typeof d === "number") {
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return null;
    const iso = parsed.toISOString().split("T")[0]; // YYYY-MM-DD
    return {
      localized: parsed.toLocaleDateString(),
      value: iso,
      culture: navigator.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      raw: parsed.getTime(),
    };
  }

  return null;
}

function getInputDateValue(localizedDateObj) {
  if (!localizedDateObj) return "";
  if (typeof localizedDateObj === "string") {
    const maybe = toLocalizedDate(localizedDateObj);
    return maybe ? maybe.value : "";
  }
  return localizedDateObj.value || "";
}

function dataURLtoFile(dataurl, filename = "photo.jpg") {
  const arr = dataurl.split(",");
  const mime = (arr[0].match(/:(.*?);/) || [])[1] || "image/jpeg";
  const bstr = atob(arr[1] || "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
